import { Link } from "react-router-dom";
import {
    ChartLineUp,
    Eye,
    UsersThree,
    Camera,
    ShieldCheck,
    Lightning,
    ArrowRight,
    CheckCircle,
    Star,
    Phone,
    Confetti,
    House,
    ShoppingCart,
    ForkKnife,
    Briefcase,
    Storefront,
    Bank,
    Heartbeat,
    Wrench,
    WhatsappLogo,
} from "@phosphor-icons/react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { PACKAGES, AGENCY_PACKAGES, INDUSTRIES, LOCATIONS, FAQS, TESTIMONIALS } from "@/lib/data";

const ICONS = {
    House,
    Heartbeat,
    Wrench,
    Confetti,
    ShoppingCart,
    ForkKnife,
    ShieldCheck,
    Bank,
    Storefront,
    Briefcase,
};

export default function Home() {
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden bg-[#0A192F] pt-32 md:pt-40 pb-24 md:pb-32 bg-grain">
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage:
                            "url('https://images.pexels.com/photos/8795934/pexels-photo-8795934.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0A192F]/95 via-[#0A192F]/85 to-[#0A192F]" />
                <div className="absolute top-1/4 -right-32 w-96 h-96 rounded-full bg-[#D4AF37]/15 blur-3xl" />
                <div className="absolute bottom-1/4 -left-32 w-96 h-96 rounded-full bg-[#25D366]/10 blur-3xl" />

                <div className="relative max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 animate-fade-up">
                        <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white/5 border border-[#D4AF37]/30 mb-6">
                            <span className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" />
                            <span className="text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">
                                Where Reach Becomes Revenue
                            </span>
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6">
                            Reach Thousands<br />
                            Through Personal<br />
                            <span className="text-gold-gradient">WhatsApp Statuses.</span>
                        </h1>
                        <p className="text-white/75 text-base md:text-lg leading-relaxed max-w-2xl mb-8">
                            Advertise where people are already paying attention. Our network of influencers and
                            everyday users helps businesses generate real visibility and real results.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link
                                to="/packages"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#D4AF37] hover:bg-[#e3c14a] text-[#0A192F] font-semibold text-base transition-all shadow-2xl shadow-[#D4AF37]/30 hover:shadow-[#D4AF37]/50 hover:scale-105"
                                data-testid="hero-get-pricing-button"
                            >
                                Get Pricing <ArrowRight size={18} weight="bold" />
                            </Link>
                            <Link
                                to="/schedule"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-white/30 text-white hover:bg-white hover:text-[#0A192F] font-semibold text-base transition-all"
                                data-testid="hero-schedule-campaign-button"
                            >
                                Schedule A Campaign
                            </Link>
                        </div>

                        <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
                            {[
                                { v: "500M+", l: "Status Views Delivered" },
                                { v: "5,000+", l: "Poster Network" },
                                { v: "1–24h", l: "To Launch Your Campaign" },
                            ].map((s) => (
                                <div key={s.l}>
                                    <div className="font-display text-2xl md:text-3xl font-bold text-[#D4AF37]">{s.v}</div>
                                    <div className="text-xs uppercase tracking-wider text-white/55 mt-1">{s.l}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Phone mockup */}
                    <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.2s" }}>
                        <div className="relative mx-auto max-w-[300px]">
                            <div className="absolute -inset-4 bg-gradient-to-tr from-[#D4AF37]/30 to-transparent blur-2xl rounded-[3rem]" />
                            <div className="relative bg-[#0A192F] rounded-[2.5rem] border-[10px] border-[#1a2942] shadow-2xl overflow-hidden">
                                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-[#0A192F] rounded-full z-10" />
                                <div className="bg-[#075E54] px-4 pt-10 pb-4">
                                    <div className="flex items-center gap-2 text-white">
                                        <WhatsappLogo size={20} weight="fill" color="#25D366" />
                                        <span className="text-sm font-semibold">Status</span>
                                    </div>
                                </div>
                                <div className="bg-white p-3 space-y-3 min-h-[420px]">
                                    {[
                                        { name: "Brand Promo", color: "from-[#D4AF37] to-[#b8951e]", txt: "Your Ad Here" },
                                        { name: "Yehuda · Monsey, NY", color: "from-pink-500 to-rose-500", txt: "Friend's Status" },
                                        { name: "CBN · Lakewood, NJ", color: "from-blue-500 to-indigo-600", txt: "Influencer Status" },
                                    ].map((c, i) => (
                                        <div key={i} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg">
                                            <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${c.color} ring-2 ring-[#25D366] p-0.5`}>
                                                <div className="w-full h-full rounded-full bg-white" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                                                <div className="text-xs text-slate-500">{c.txt}</div>
                                            </div>
                                        </div>
                                    ))}
                                    <div className="mt-4 p-4 rounded-xl bg-[#0A192F] text-white">
                                        <div className="text-[10px] uppercase tracking-wider text-[#D4AF37] mb-1">Verified Reach</div>
                                        <div className="font-display text-2xl font-bold">18,420 views</div>
                                        <div className="text-xs text-white/60 mt-1">Across 100+ statuses</div>
                                    </div>
                                </div>
                            </div>
                            <div className="absolute -right-6 -top-4 px-3 py-2 rounded-xl bg-white shadow-2xl border border-[#D4AF37]/20 animate-fade-up" style={{ animationDelay: "0.6s" }}>
                                <div className="flex items-center gap-1.5">
                                    <CheckCircle size={16} color="#25D366" weight="fill" />
                                    <span className="text-xs font-semibold text-slate-700">Screenshot Proof</span>
                                </div>
                            </div>
                            <div className="absolute -left-8 bottom-12 px-3 py-2 rounded-xl bg-white shadow-2xl border border-slate-200 animate-fade-up" style={{ animationDelay: "0.8s" }}>
                                <div className="flex items-center gap-1.5">
                                    <ChartLineUp size={16} color="#D4AF37" weight="fill" />
                                    <span className="text-xs font-semibold text-slate-700">+340% Reach</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHY US — Bento */}
            <section className="bg-white py-24 md:py-32" data-testid="why-us-section">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <div className="max-w-3xl mb-14">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Why CashFlow Ads</div>
                        <h2 className="font-display text-3xl md:text-5xl font-bold text-[#0A192F] tracking-tight">
                            Traditional ads are ignored.<br />
                            <span className="text-slate-400">Personal statuses get seen.</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
                        <div className="md:col-span-7 bg-[#0A192F] rounded-3xl p-8 md:p-10 text-white relative overflow-hidden card-lift">
                            <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-[#D4AF37]/15 blur-3xl" />
                            <Eye size={36} color="#D4AF37" weight="duotone" />
                            <h3 className="font-display text-2xl md:text-3xl font-semibold mt-4 mb-3">Real human reach</h3>
                            <p className="text-white/70 leading-relaxed max-w-md">
                                No bots, no fake impressions, no artificial engagement. Every view comes from a real person opening their WhatsApp.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-2">
                                {["No bots", "No fake views", "Verified posters"].map((t) => (
                                    <span key={t} className="px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs">{t}</span>
                                ))}
                            </div>
                        </div>

                        <div className="md:col-span-5 bg-[#F8FAFC] rounded-3xl p-8 md:p-10 border border-slate-100 card-lift">
                            <Camera size={36} color="#0A192F" weight="duotone" />
                            <h3 className="font-display text-2xl font-semibold mt-4 mb-3 text-[#0A192F]">Screenshot verification</h3>
                            <p className="text-slate-600 leading-relaxed">
                                Every advertiser receives live-account screenshot proof from each poster — full transparency, every campaign.
                            </p>
                        </div>

                        <div className="md:col-span-5 bg-[#F8FAFC] rounded-3xl p-8 md:p-10 border border-slate-100 card-lift">
                            <UsersThree size={36} color="#0A192F" weight="duotone" />
                            <h3 className="font-display text-2xl font-semibold mt-4 mb-3 text-[#0A192F]">Influencer network</h3>
                            <p className="text-slate-600 leading-relaxed">
                                5,000+ vetted posters and micro-influencers across the US, Canada, UK, and Israel — distributed manually for authenticity.
                            </p>
                        </div>

                        <div className="md:col-span-7 bg-gradient-to-br from-[#D4AF37] to-[#b8951e] rounded-3xl p-8 md:p-10 text-[#0A192F] card-lift">
                            <Lightning size={36} weight="duotone" />
                            <h3 className="font-display text-2xl md:text-3xl font-semibold mt-4 mb-3">Campaigns launch in 1–24 hours</h3>
                            <p className="leading-relaxed max-w-md text-[#0A192F]/80">
                                Submit your creative, pick your audience, and your ad starts appearing in personal status feeds — fast.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="bg-[#F8FAFC] py-24 md:py-32" data-testid="how-it-works-section">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">How It Works</div>
                        <h2 className="font-display text-3xl md:text-5xl font-bold text-[#0A192F]">A 4-step process — built for speed.</h2>
                    </div>
                    <div className="grid md:grid-cols-4 gap-5">
                        {[
                            { n: "01", t: "Submit", d: "Send us your flyer, image, link, or promotion." },
                            { n: "02", t: "Distribute", d: "Our network manually posts your ad on real WhatsApp statuses." },
                            { n: "03", t: "24h Live", d: "Your ad stays active across statuses for a minimum of 24 hours." },
                            { n: "04", t: "Verify", d: "Receive screenshot proof and full campaign analytics." },
                        ].map((s, i) => (
                            <div key={s.n} className="relative bg-white rounded-2xl p-7 border border-slate-100 card-lift">
                                <div className="font-display text-5xl font-bold text-[#D4AF37]/30">{s.n}</div>
                                <h3 className="font-display text-xl font-semibold mt-2 text-[#0A192F]">{s.t}</h3>
                                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{s.d}</p>
                                {i < 3 && (
                                    <ArrowRight size={20} className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section className="bg-white py-24 md:py-32" id="pricing" data-testid="pricing-section">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Campaign Packages</div>
                        <h2 className="font-display text-3xl md:text-5xl font-bold text-[#0A192F]">Pricing built around your reach.</h2>
                        <p className="text-slate-600 mt-4">Five clear tiers — pick one, scale up anytime.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
                        {PACKAGES.map((p) => (
                            <div
                                key={p.id}
                                className={`relative rounded-2xl p-7 card-lift ${
                                    p.featured
                                        ? "bg-[#0A192F] text-white border-2 border-[#D4AF37] lg:scale-105 shadow-2xl shadow-[#D4AF37]/20"
                                        : "bg-white border border-slate-200"
                                }`}
                                data-testid={`pricing-card-${p.id}`}
                            >
                                {p.featured && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#D4AF37] text-[#0A192F] text-[10px] font-bold tracking-wider uppercase">
                                        Most Popular
                                    </div>
                                )}
                                <div className="text-xs uppercase tracking-wider opacity-60 mb-1">{p.tagline}</div>
                                <h3 className={`font-display text-xl font-bold mb-3 ${p.featured ? "text-white" : "text-[#0A192F]"}`}>{p.name}</h3>
                                <div className="flex items-baseline gap-1 mb-1">
                                    <span className={`font-display text-4xl font-bold ${p.featured ? "text-[#D4AF37]" : "text-[#0A192F]"}`}>${p.price}</span>
                                </div>
                                <div className={`text-sm font-semibold mb-1 ${p.featured ? "text-white/90" : "text-slate-700"}`}>{p.views} views</div>
                                <div className={`text-xs mb-5 ${p.featured ? "text-white/60" : "text-slate-500"}`}>{p.influencers}</div>
                                <ul className="space-y-2 mb-6">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2 text-xs">
                                            <CheckCircle size={14} weight="fill" className={p.featured ? "text-[#D4AF37] mt-0.5" : "text-[#0A192F] mt-0.5"} />
                                            <span className={p.featured ? "text-white/85" : "text-slate-600"}>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/schedule"
                                    className={`block text-center w-full py-2.5 rounded-full font-semibold text-sm transition-all ${
                                        p.featured
                                            ? "bg-[#D4AF37] text-[#0A192F] hover:bg-[#e3c14a]"
                                            : "bg-[#0A192F] text-white hover:bg-[#112240]"
                                    }`}
                                    data-testid={`pricing-cta-${p.id}`}
                                >
                                    Schedule Campaign
                                </Link>
                            </div>
                        ))}
                    </div>

                    {/* Agency packages */}
                    <div className="mt-20" data-testid="agency-packages-home">
                        <div className="text-center mb-8">
                            <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Agency Packages</div>
                            <h3 className="font-display text-2xl md:text-3xl font-bold text-[#0A192F]">For agencies &amp; enterprise scale.</h3>
                            <p className="text-slate-600 text-sm mt-2">Higher volume tiers built for marketing teams and larger campaigns.</p>
                        </div>
                        <div className="overflow-x-auto -mx-4 md:mx-0">
                            <div className="min-w-[560px] px-4 md:px-0">
                                <table className="w-full text-sm rounded-2xl overflow-hidden border border-slate-200">
                                    <thead className="bg-[#0A192F] text-white">
                                        <tr>
                                            <th className="py-3 px-4 md:px-6 text-left font-semibold whitespace-nowrap">Price</th>
                                            <th className="py-3 px-4 md:px-6 text-left font-semibold whitespace-nowrap">Posters / Influencers</th>
                                            <th className="py-3 px-4 md:px-6 text-left font-semibold whitespace-nowrap">Est. Views</th>
                                            <th className="py-3 px-4 md:px-6 text-right"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-slate-100">
                                        {AGENCY_PACKAGES.map((p, i) => (
                                            <tr key={p.id} className={i % 2 === 1 ? "bg-slate-50/50" : ""} data-testid={`agency-row-${p.id}`}>
                                                <td className="py-3 px-4 md:px-6 font-display text-lg md:text-xl font-bold text-[#D4AF37] whitespace-nowrap">${p.price.toLocaleString()}</td>
                                                <td className="py-3 px-4 md:px-6 text-slate-700 whitespace-nowrap">{p.influencers}</td>
                                                <td className="py-3 px-4 md:px-6 font-semibold text-[#0A192F] whitespace-nowrap">{p.views}</td>
                                                <td className="py-3 px-4 md:px-6 text-right whitespace-nowrap">
                                                    <Link to="/schedule" className="inline-flex items-center gap-1 text-[#0A192F] font-semibold text-xs hover:text-[#D4AF37] transition-colors" data-testid={`agency-cta-${p.id}`}>
                                                        Book <ArrowRight size={12} />
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="md:hidden text-center text-[10px] text-slate-400 mt-2 uppercase tracking-wider">← Swipe to see more →</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PROMOTIONS */}
            <section className="bg-[#0A192F] py-24 md:py-32 relative overflow-hidden bg-grain" data-testid="promotions-section">
                <div className="absolute top-1/3 right-0 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-3xl" />
                <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Special Promotions</div>
                        <h2 className="font-display text-3xl md:text-5xl font-bold text-white">Stack the savings.</h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-[#D4AF37]/15 to-transparent border border-[#D4AF37]/30 rounded-3xl p-8 md:p-10 card-lift">
                            <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">Limited Offer</div>
                            <h3 className="font-display text-3xl md:text-4xl font-bold text-white mb-3">Buy 2, Get 1 Free</h3>
                            <p className="text-white/70 leading-relaxed mb-6">
                                Purchase 2 Viral Packages and receive a 3rd Viral Package on us. That's 60,000 verified views for the price of 40,000.
                            </p>
                            <div className="flex items-end gap-3">
                                <span className="font-display text-5xl font-bold text-[#D4AF37]">$1,000</span>
                                <span className="text-white/50 line-through pb-2">$1,500</span>
                            </div>
                            <Link to="/schedule" className="mt-6 inline-flex items-center gap-2 text-[#D4AF37] font-semibold hover:gap-3 transition-all" data-testid="promo-2x1-cta">
                                Claim deal <ArrowRight size={16} />
                            </Link>
                        </div>

                        <div className="bg-white rounded-3xl p-8 md:p-10 card-lift">
                            <div className="text-xs tracking-[0.2em] uppercase text-[#0A192F] mb-3">Enterprise Savings</div>
                            <h3 className="font-display text-3xl md:text-4xl font-bold text-[#0A192F] mb-3">10× Viral Packages</h3>
                            <p className="text-slate-600 leading-relaxed mb-6">
                                Up to 200,000 verified status views. Built for agencies, brand managers, and enterprise growth teams.
                            </p>
                            <div className="grid grid-cols-3 gap-3 mb-6">
                                <div>
                                    <div className="text-xs text-slate-500">Regular</div>
                                    <div className="font-display text-2xl font-bold text-slate-400 line-through">$5,000</div>
                                </div>
                                <div>
                                    <div className="text-xs text-slate-500">You pay</div>
                                    <div className="font-display text-2xl font-bold text-[#0A192F]">$2,975</div>
                                </div>
                                <div>
                                    <div className="text-xs text-[#25D366]">Save</div>
                                    <div className="font-display text-2xl font-bold text-[#25D366]">$2,025</div>
                                </div>
                            </div>
                            <Link to="/schedule" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0A192F] text-white font-semibold text-sm" data-testid="promo-enterprise-cta">
                                Talk to sales <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* AUDIENCE TARGETING */}
            <section className="bg-white py-24 md:py-32" data-testid="targeting-section">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <div className="grid md:grid-cols-2 gap-12 items-start mb-14">
                        <div>
                            <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Audience Targeting</div>
                            <h2 className="font-display text-3xl md:text-5xl font-bold text-[#0A192F]">Real people. Real geographies.</h2>
                        </div>
                        <div className="text-slate-600 leading-relaxed">
                            Target by location, age, gender, profession, business owners, or community groups. We segment your audience the way a marketing team would — manually, deliberately, and with full transparency.
                        </div>
                    </div>

                    <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-9 gap-3 mb-12">
                        {LOCATIONS.map((l) => (
                            <div key={l.name} className="flex flex-col items-center justify-center bg-[#F8FAFC] hover:bg-[#0A192F] hover:text-white transition-all rounded-2xl p-4 border border-slate-100 card-lift cursor-default" data-testid={`location-${l.name.toLowerCase().replace(/\s+/g, "-")}`}>
                                <div className="text-2xl mb-1.5">{l.flag}</div>
                                <div className="text-xs font-semibold text-center">{l.name}</div>
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        {["Age", "Gender", "Profession", "Business Owners", "Community Groups", "Geographic"].map((t) => (
                            <div key={t} className="px-4 py-3 rounded-xl bg-[#0A192F] text-white text-sm font-medium text-center">{t}</div>
                        ))}
                    </div>
                </div>
            </section>

            {/* INDUSTRIES */}
            <section className="bg-[#F8FAFC] py-24 md:py-32" data-testid="industries-section">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Industries We Power</div>
                        <h2 className="font-display text-3xl md:text-5xl font-bold text-[#0A192F]">Built for businesses that want results.</h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {INDUSTRIES.map((ind) => {
                            const Icon = ICONS[ind.icon] || House;
                            return (
                                <div key={ind.name} className="bg-white rounded-2xl p-6 border border-slate-100 card-lift flex flex-col items-center text-center" data-testid={`industry-${ind.name.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <Icon size={32} weight="duotone" color="#D4AF37" />
                                    <div className="mt-3 font-semibold text-sm text-[#0A192F]">{ind.name}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FOUNDER'S MESSAGE */}
            <section className="bg-[#0A192F] py-24 md:py-32 relative overflow-hidden bg-grain" data-testid="founder-section">
                <div className="absolute top-1/3 -left-32 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-3xl" />
                <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full bg-[#D4AF37]/5 blur-3xl" />
                <div className="relative max-w-3xl mx-auto px-6 md:px-8">
                    <div className="text-center mb-10">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">A Message From The Founder</div>
                        <h2 className="font-display text-3xl md:text-5xl font-bold text-white">People trust people<br /><span className="text-gold-gradient">more than ads.</span></h2>
                    </div>
                    <div className="bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12 relative">
                        <div className="absolute -top-4 left-8 font-display text-7xl text-[#D4AF37] leading-none select-none">"</div>
                        <div className="space-y-5 text-white/80 leading-relaxed text-base md:text-lg font-display">
                            <p>CashFlow Ads was built on a simple belief: <span className="text-white font-semibold">people trust people more than advertisements.</span></p>
                            <p>For years, businesses have spent money on traditional ads that are shown to strangers scrolling through crowded feeds. While those methods can work, I believed there was a better way.</p>
                            <p className="text-[#D4AF37] italic">What if businesses could get their message in front of real people through real relationships?</p>
                            <p>That's exactly why I started CashFlow Ads.</p>
                            <p>Our platform connects businesses with everyday individuals who share advertisements on their personal WhatsApp statuses and social media stories. When someone posts an ad through CashFlow Ads, it isn't being shown to a random audience. It's being shown to friends, family members, coworkers, neighbors, and acquaintances who already know and trust that person.</p>
                            <p>This creates a more personal and authentic advertising experience that traditional advertising simply cannot replicate.</p>
                            <p>At the same time, CashFlow Ads gives individuals the opportunity to earn money by sharing approved advertisements with their network — creating value for both businesses and posters.</p>
                            <p>What started as a simple idea has grown into a nationwide network helping businesses gain exposure while helping people earn extra income.</p>
                            <p>Thank you for visiting CashFlow Ads. We're excited to help your business reach more people through the power of personal connections.</p>
                        </div>
                        <div className="mt-10 pt-6 border-t border-white/10 flex items-center gap-4">
                            <img src="/logo.png" alt="CashFlow Ads" className="w-12 h-12 rounded-full object-cover bg-white ring-2 ring-[#D4AF37]/40" />
                            <div>
                                <div className="font-display text-lg font-bold text-white tracking-tight">— The Founder</div>
                                <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">CashFlow Ads</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="bg-white py-24 md:py-32" data-testid="testimonials-section">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Testimonials</div>
                        <h2 className="font-display text-3xl md:text-5xl font-bold text-[#0A192F]">What our clients are saying.</h2>
                        <div className="flex items-center justify-center gap-1 mt-5">
                            {[...Array(5)].map((_, k) => (
                                <Star key={k} size={20} weight="fill" color="#D4AF37" />
                            ))}
                            <span className="ml-2 text-sm font-semibold text-[#0A192F]">5.0 · Trusted by the community</span>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {TESTIMONIALS.map((t, i) => (
                            <div key={i} className="bg-[#F8FAFC] rounded-2xl p-6 border border-slate-100 card-lift flex flex-col" data-testid={`testimonial-${i}`}>
                                <div className="flex gap-0.5 mb-3">
                                    {[...Array(5)].map((_, k) => (
                                        <Star key={k} size={14} weight="fill" color="#D4AF37" />
                                    ))}
                                </div>
                                <p className="text-slate-700 leading-relaxed mb-5 text-sm flex-1">"{t.quote}"</p>
                                <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0A192F] to-[#1a2942] flex items-center justify-center text-white font-display font-bold flex-shrink-0">
                                        {t.name.charAt(0)}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-[#0A192F] truncate">{t.name}</div>
                                        <div className="text-xs text-slate-500 truncate">{t.role}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-[#F8FAFC] py-24 md:py-32" data-testid="faq-section">
                <div className="max-w-3xl mx-auto px-6 md:px-8">
                    <div className="text-center mb-12">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Frequently Asked</div>
                        <h2 className="font-display text-3xl md:text-5xl font-bold text-[#0A192F]">Answers, before you ask.</h2>
                    </div>
                    <Accordion type="single" collapsible className="space-y-3">
                        {FAQS.map((f, i) => (
                            <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-2xl border border-slate-100 px-6" data-testid={`faq-item-${i}`}>
                                <AccordionTrigger className="font-display text-base md:text-lg font-semibold text-[#0A192F] hover:no-underline text-left">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-[#0A192F] py-20 md:py-28 relative overflow-hidden">
                <div className="absolute inset-0 bg-grain" />
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/10 blur-3xl" />
                <div className="relative max-w-4xl mx-auto px-6 md:px-8 text-center">
                    <img src="/logo.png" alt="CashFlow Ads" className="mx-auto w-20 h-20 md:w-24 md:h-24 rounded-full object-cover bg-white shadow-2xl ring-4 ring-[#D4AF37]/40 mb-6" />
                    <div className="font-display text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-3">CashFlow Ads</div>
                    <h2 className="font-display text-3xl md:text-5xl font-bold text-white mb-5">
                        Ready to put your brand in front of <span className="text-gold-gradient">real people?</span>
                    </h2>
                    <p className="text-white/70 mb-8 max-w-2xl mx-auto">Get started in minutes. Talk to our team or launch your first campaign today.</p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Link to="/schedule" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#D4AF37] text-[#0A192F] font-semibold hover:bg-[#e3c14a] transition-all shadow-2xl shadow-[#D4AF37]/30" data-testid="final-cta-launch">
                            Launch My Campaign <ArrowRight size={18} weight="bold" />
                        </Link>
                        <a href="https://wa.me/18457295009" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#25D366] text-white font-semibold hover:bg-[#1ebe5b] transition-all" data-testid="final-cta-whatsapp">
                            <WhatsappLogo size={18} weight="fill" /> WhatsApp Us
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
